import React, { useEffect, useRef, useState } from "react";
import HTMLFlipBook from "react-pageflip";
import { getEditions, getEdition } from "../lib/api";
import { BookOpen, ChevronLeft, ChevronRight, X, Calendar } from "lucide-react";

export default function EEdition() {
  const [editions, setEditions] = useState([]);
  const [active, setActive] = useState(null);

  useEffect(() => { getEditions().then(setEditions).catch(() => {}); }, []);
  const open = async (id) => { const e = await getEdition(id); setActive(e); };

  return (
    <div className="paper-texture min-h-screen">
      <div className="border-b border-[var(--paper-border)] bg-[var(--navy)]">
        <div className="mx-auto max-w-7xl px-4 py-10">
          <p className="eyebrow flex items-center gap-2 text-[var(--gold)]"><BookOpen size={14} /> Digital Replica Edition</p>
          <h1 className="font-display text-4xl font-extrabold text-white sm:text-5xl">The e-Edition</h1>
          <p className="font-serif-body mt-2 max-w-2xl text-white/80">Read a page-turning replica of the printed News-Leader, and browse our archive of past issues.</p>
        </div>
      </div>

      <div className="mx-auto max-w-7xl px-4 py-10">
        <h2 className="section-rule font-display pt-3 text-2xl font-bold text-[var(--navy)]">Issue Archive</h2>
        <div className="mt-6 grid gap-8 sm:grid-cols-3 lg:grid-cols-4">
          {editions.map((e) => (
            <button key={e.id} onClick={() => open(e.id)} data-testid={`edition-${e.id}`} className="card-hover group text-left">
              <div className="img-zoom overflow-hidden rounded-lg border border-[var(--paper-border)] shadow-md">
                <img src={e.cover} alt={e.title} className="h-80 w-full object-cover object-top" />
              </div>
              <p className="font-ui mt-3 flex items-center gap-1.5 text-xs text-slate-500"><Calendar size={12} className="text-[var(--gold)]" /> {e.issue_date}</p>
              <h3 className="font-display text-lg font-bold leading-snug text-[var(--navy)] group-hover:underline">{e.title}</h3>
              <span className="font-ui text-xs font-semibold uppercase tracking-wider text-[var(--gold)]">Read this issue →</span>
            </button>
          ))}
        </div>
      </div>

      {active && <FlipViewer edition={active} onClose={() => setActive(null)} />}
    </div>
  );
}

function FlipViewer({ edition, onClose }) {
  const book = useRef(null);
  const [dims, setDims] = useState({ w: 420, h: 620 });

  useEffect(() => {
    const calc = () => {
      const vw = window.innerWidth, vh = window.innerHeight;
      const w = Math.min(vw < 768 ? vw - 40 : (vw - 120) / 2, 480);
      setDims({ w: Math.max(260, w), h: Math.max(260, w) * 1.49 });
    };
    calc();
    window.addEventListener("resize", calc);
    return () => window.removeEventListener("resize", calc);
  }, []);

  return (
    <div className="fixed inset-0 z-[70] flex flex-col bg-[#1a1a1a]/97" data-testid="flip-viewer">
      <div className="flex items-center justify-between px-5 py-3 text-white">
        <p className="font-display text-lg font-bold">{edition.title}</p>
        <button onClick={onClose} data-testid="flip-close" className="text-white/80 hover:text-white"><X size={26} /></button>
      </div>
      <div className="flex flex-1 items-center justify-center overflow-hidden px-2">
        <button onClick={() => book.current?.pageFlip()?.flipPrev()} className="hidden shrink-0 px-3 text-white/70 hover:text-white sm:block" data-testid="flip-prev"><ChevronLeft size={40} /></button>
        <HTMLFlipBook
          ref={book}
          width={dims.w}
          height={dims.h}
          size="fixed"
          minWidth={260}
          maxWidth={480}
          minHeight={400}
          maxHeight={720}
          showCover={true}
          mobileScrollSupport={true}
          className="flipbook"
        >
          {edition.pages.map((src, i) => (
            <div key={i} className="bg-white" data-density={i === 0 ? "hard" : "soft"}>
              <img src={src} alt={`Page ${i + 1}`} className="h-full w-full object-contain" />
            </div>
          ))}
        </HTMLFlipBook>
        <button onClick={() => book.current?.pageFlip()?.flipNext()} className="hidden shrink-0 px-3 text-white/70 hover:text-white sm:block" data-testid="flip-next"><ChevronRight size={40} /></button>
      </div>
      <p className="pb-4 text-center font-ui text-xs text-white/50">Drag a page corner or use the arrows to turn pages · {edition.pages.length} pages</p>
    </div>
  );
}
